import { clearAssetsSelection } from 'actions/layer'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Selectors from 'selectors'
import { setPreviewParams as setPreviewParamsAction } from '~/actions/preview'
import {
  useAggregatedPlayer,
  useCanvasParams,
  useTimelineVideoPlayerAssets
} from '~/components/Preview/lib'
import { CircularProgress } from '~/components/base/CircularProgress/CircularProgress'
import { useAction, useChanged } from '~/hooks'
import { AudioAsset, VideoAsset } from '~/models/Asset'
import { PREVIEW_I_O_POINTS_TRIM_HEIGHT } from '~/constant'
import { playerLog } from '~/Util/playerLog'
import { PreviewPlayerContext } from '~/contexts/PreviewPlayerContext'
import AssetsOverlay from '../AssetsOverlay/AssetsOverlay'
import { MediaPlayers } from './MediaPlayers'


export default function AggregatedPlayer(props) {
  const {
    playing: basePlaying,
    isMedia,
    ...rest
  } = props
  const { width, height: initHeight } = rest
  const {
    playingMediaAssets,
    backgroundAssets,
    composedOverlayAssets,
    composedMediaAssets,
    overlayTransitions,
  } = useSelector(Selectors.selectTimelinePreviewData)

  const isSliderOnBufferingPos = useSelector(state => state.timeline.isSliderOnBufferingPos)
  const height = initHeight - PREVIEW_I_O_POINTS_TRIM_HEIGHT
  const transitionVideoAsset = composedMediaAssets[0]

  const setPreviewParams = useAction(setPreviewParamsAction)
  const clearSelection = useAction(clearAssetsSelection)

  const mediaRecordingAssetInProgress = useSelector(Selectors.recording.isRecordingStarted)

  const { setCanvasSource } = useContext(PreviewPlayerContext)
  const params = useCanvasParams({ width, height })
  const scale = useChanged(params.scale)
  const boundingRect = useChanged(params.rect)

  const toVideoAudioPlayerIds = useCallback(assets => {
    const toPlayerIds = (klass, assets) => klass.filter(assets).map(asset => asset.id)
    return [ ...toPlayerIds(VideoAsset, assets), ...toPlayerIds(AudioAsset, assets) ]
  }, [])

  const playingPlayerIds = useMemo(() => toVideoAudioPlayerIds(playingMediaAssets),
    [ playingMediaAssets, toVideoAudioPlayerIds ])
  const backgroundPlayerIds = useMemo(() => toVideoAudioPlayerIds(backgroundAssets),
    [ backgroundAssets, toVideoAudioPlayerIds ])
  const composedPlayerIds = useMemo(() => toVideoAudioPlayerIds(composedMediaAssets),
    [ composedMediaAssets, toVideoAudioPlayerIds ])

  const mode = mediaRecordingAssetInProgress ? 'test' : 'default'

  const {
    isBuffering, onMounted, onBufferingProgress, isPlaying, isReady,
  } = useAggregatedPlayer(playingPlayerIds.concat(composedPlayerIds), backgroundPlayerIds,
    isSliderOnBufferingPos, basePlaying, mode)

  useEffect(() => {
    playerLog('[AggregatedPlayer] asset sets changed', {
      playingIds: playingMediaAssets.map(a => a.id),
      playingFirstId: playingMediaAssets[0]?.id,
      backgroundIds: backgroundAssets.map(a => a.id),
      composedIds: composedMediaAssets.map(a => a.id),
      isPlaying,
      isBuffering,
      isReady,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ playingMediaAssets, backgroundAssets, composedMediaAssets ])

  useEffect(() => {
    playerLog('[AggregatedPlayer] playing state changed', {
      isPlaying,
      isBuffering,
      isReady,
      playingIds: playingPlayerIds,
    })
  }, [ isPlaying, isBuffering, isReady, playingPlayerIds ])

  const {
    renderingAssets,
    videoRefs,
    getTimelineAssetVideoPlayerProps,
    overlayAssets,
    addTimelinePlayers,
  } = useTimelineVideoPlayerAssets({
    playingMediaAssets,
    backgroundAssets,
    composedMediaAssets,
    onMounted,
    onBufferingProgress,
    transitionVideoAsset,
    isPlaying,
    rect: boundingRect,
    restAggregatedPlayerProps: rest,
  })

  useEffect(() => {
    setPreviewParams(scale, boundingRect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ scale, boundingRect ])

  const handleClickVideo = useCallback(e => {
    if (e.target.tagName === 'DIV') {
      clearSelection()
    }
  }, [ clearSelection ])

  const memoizedAssetsProps = useMemo(() => renderingAssets.map(asset => ({
    key: asset.id,
    isMedia,
    addTimelinePlayers,
    ...getTimelineAssetVideoPlayerProps(asset,
      backgroundAssets.some(a => a.id === asset.id),
      composedOverlayAssets.some(a => a.id === asset.id)),
  })),
  [ renderingAssets, isMedia, addTimelinePlayers, composedOverlayAssets,
    getTimelineAssetVideoPlayerProps, backgroundAssets ])

  return (
    <>
      <MediaPlayers renderingAssetsProps={memoizedAssetsProps} />
      {!!overlayAssets.length && (
        <div
          onClick={handleClickVideo}
          className="preview-player__text-overlay"
          style={{ width, height, zIndex: 55 }}
        >
          <AssetsOverlay
            overlayAssets={overlayAssets}
            composedOverlayAssets={composedOverlayAssets}
            overlayTransitions={overlayTransitions}
            width={width}
            height={height}
            videoRefs={videoRefs}
            setCanvasSource={setCanvasSource}
            boundingRect={boundingRect}
            scale={scale}
          />
        </div>
      )}
      <If condition={isBuffering}>
        <div className="progress-spinner">
          <CircularProgress
            size={100}
            endless
            percents={50}
            transparent
          />
        </div>
      </If>
    </>
  )
}
